import HubLeadCTA from "@/components/HubLeadCTA";
import { ctaDesc, ctaTitle } from "@/lib/seo";
import { createFileRoute, Link } from "@tanstack/react-router";
import { SWORN_TRANSLATOR_PRODUCTS, SWORN_PRODUCT_CATEGORIES, SWORN_CATALOG_PRICE_MIN, SWORN_CATALOG_PRICE_MAX, SWORN_TURNAROUND_STD } from "@/data/sworn-translator-products";
import { BRAND_URL } from "@/lib/brand";
import { ArrowRight, Languages, ShieldCheck, Clock } from "lucide-react";
import { ProductCatalogHubNav } from "@/components/ProductCatalogHubNav";

const PATH = "/sworn-translator/products";
const URL = `${BRAND_URL}${PATH}`;

export const Route = createFileRoute("/sworn-translator/products/")({
  head: () => ({
    meta: [
      { title: ctaTitle("แปลสาบาน (Sworn Translation) — Catalog | NYC", "/sworn-translator/products/index", "th")},
      { name: "description", content: ctaDesc("แปลสาบานครบวงจร: นักแปล MoJ + เครือข่าย sworn translator ปลายทาง (DE/FR/ES/IT/NL/PL) · Bundle Notary + MFA + Apostille — แจ้งเอกสารที่มี รู้ขั้นตอนและระยะเวลาใน 10 นาที", "/sworn-translator/products/index", "th")},
      { property: "og:title", content: ctaTitle("Sworn Translation Product Catalog | NYC", "/sworn-translator/products/index", "th")},
      { property: "og:description", content: ctaDesc("MoJ-registered Thai translators + destination sworn-translator network for DE / FR / ES / IT / NL / PL.", "/sworn-translator/products/index", "th")},
      { property: "og:url", content: URL },
      { property: "og:locale", content: "th_TH" },
    ],
    links: [
      { rel: "canonical", href: URL },
      { rel: "alternate", hrefLang: "th-TH", href: URL },
      { rel: "alternate", hrefLang: "en", href: `${BRAND_URL}/en/sworn-translator/products` },
      { rel: "alternate", hrefLang: "x-default", href: URL },
    ],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify({
        "@context": "https://schema.org", "@type": "ItemList",
        "@id": `${URL}#catalog`,
        name: "Sworn Translation Product Catalog",
        numberOfItems: SWORN_TRANSLATOR_PRODUCTS.length,
        itemListElement: SWORN_TRANSLATOR_PRODUCTS.map((p, i) => ({
          "@type": "ListItem", position: i + 1,
          url: `${BRAND_URL}/sworn-translator/products/${p.slug}`,
          name: p.nameTh,
        })),
      })},
    ],
  }),
  component: () => (
    <main className="min-h-screen bg-background pb-20">
      <section className="mx-auto max-w-6xl px-4 pt-10">
        <div className="flex flex-wrap gap-2 text-[11px] font-bold uppercase tracking-widest text-purple-700">
          <span className="rounded-full bg-purple-100 px-3 py-1">Sworn Translator · MoJ · Destination sworn</span>
        </div>
        <h1 className="mt-3 font-display text-4xl font-extrabold text-navy-deep sm:text-5xl">
          บริการแปลสาบาน (Sworn Translation)
        </h1>
        <p className="mt-3 max-w-3xl text-lg text-slate-600">
          รายการบริการ {SWORN_TRANSLATOR_PRODUCTS.length} SKU — นักแปลจดทะเบียนกระทรวงยุติธรรมไทย + เครือข่าย sworn translator ปลายทาง (§189 GVG เยอรมัน · Cour d'appel ฝรั่งเศส · MAEC สเปน · Tribunale อิตาลี · Wbtv ดัตช์ · tłumacz przysięgły โปแลนด์) · <strong>Bundle Notary + MFA + Apostille ในสำนักงานเดียว</strong>
        </p>

        <div className="mt-6 grid gap-3 sm:grid-cols-3">
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-4">
            <ShieldCheck className="mb-2 h-5 w-5 text-emerald-700" />
            <div className="font-display font-bold text-emerald-900">นักแปลจดทะเบียน MoJ ไทย</div>
            <div className="text-xs text-slate-600">MoJ-registered Sworn Translator</div>
          </div>
          <div className="rounded-xl border border-purple-200 bg-purple-50/40 p-4">
            <Clock className="mb-2 h-5 w-5 text-purple-700" />
            <div className="font-display font-bold text-purple-900">{SWORN_TURNAROUND_STD}</div>
            <div className="text-xs text-slate-600">มาตรฐาน · เร่งด่วน 24 ชม.</div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50/40 p-4">
            <Languages className="mb-2 h-5 w-5 text-slate-700" />
            <div className="font-display font-bold text-slate-900">20+ ภาษา</div>
            <div className="text-xs text-slate-600">EN/ZH/JA/KO/DE/FR/ES/IT/AR + more</div>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-2 text-xs">
          {SWORN_PRODUCT_CATEGORIES.map((c) => (
            <a key={c.id} href={`#cat-${c.id}`} className="rounded-full border border-purple-200 bg-white px-3 py-1.5 font-semibold text-purple-800 hover:bg-purple-50">
              {c.labelTh} <span className="opacity-60">({c.count})</span>
            </a>
          ))}
        </div>
      </section>

      {SWORN_PRODUCT_CATEGORIES.map((c) => {
        const items = SWORN_TRANSLATOR_PRODUCTS.filter((p) => p.category === c.id);
        return (
          <section key={c.id} id={`cat-${c.id}`} className="mx-auto mt-12 max-w-6xl px-4">
            <h2 className="font-display text-2xl font-extrabold text-navy-deep">{c.labelTh}</h2>
            <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {items.map((p) => (
                <Link
                  key={p.sku}
                  to="/sworn-translator/products/$slug"
                  params={{ slug: p.slug }}
                  className="group rounded-2xl border border-slate-200 bg-white p-5 transition hover:-translate-y-0.5 hover:border-purple-400 hover:shadow-md"
                >
                  <div className="text-[10px] font-bold uppercase tracking-widest text-purple-700">{p.categoryLabelTh}</div>
                  <div className="mt-1 font-display text-lg font-extrabold text-navy-deep group-hover:text-purple-700">{p.nameTh}</div>
                  <div className="mt-1 text-xs text-slate-500">{p.nameEn}</div>
                  <div className="mt-3 font-display text-lg font-extrabold text-emerald-800">
                    THB {p.priceMin.toLocaleString()}–{p.priceMax.toLocaleString()}
                  </div>
                  <div className="text-xs text-slate-500">{p.turnaround}</div>
                  <div className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-purple-700">
                    ดูรายละเอียด <ArrowRight className="h-3 w-3" />
                  </div>
                </Link>
              ))}
            </div>
          </section>
        );
      })}
      <ProductCatalogHubNav locale="th" current="sworn" />
    <HubLeadCTA locale="th" service="Sworn Translator" />
      </main>
  ),
});
